import {type SourceVideoReplacementAuthority} from './source-video-replacement';
import {commitSourceSceneOutput,receiveSourceSceneOutput} from './source-scene-delivery';
import {useEffect,useRef,useState,type RefObject} from 'react';
import {flushSync} from 'react-dom';
import type {PlayerRef} from '@remotion/player';
import type {SubscribeUploadedMedia} from '../../protocol/work-session';
import {makeItem} from '../../vendor/src/features/remotion-editor/editor/items/make-item';
import {useCurrentStateAsRef,useWriteContext} from '../../vendor/src/features/remotion-editor/editor/utils/use-context';
import {hasPendingTimelineEdit} from './edit-ownership';
import {planGeneratedContinuation} from './generated-continuation';
import {planGeneratedVideoDraft} from './generated-video-draft';
import {readVideoGenerationReceipt} from '../../protocol/video-generation-receipt';
import {planGeneratedSpeech} from './generated-speech';
import {planFreeSpeech} from './free-speech';
import {isCompanyVoiceClip,type CompanyVoiceClip} from '../../protocol/voice-replacement';
import {retainVoiceReplacement} from './voice-replacement';
import {VoiceReplacementChooser} from './VoiceReplacementChooser';
import {retainCompanyLibraryAsset} from './library-source';
import {SceneVoiceChooser} from './SceneVoiceChooser';
import {readSceneVoiceWords,type SceneVoiceAttachment} from '../../protocol/scene-voice';
import {blockSceneVoiceStage} from '../host/scene-voice';

export function ReceiveUploadedMedia({subscribe,playerRef,authority}:{subscribe:SubscribeUploadedMedia;playerRef:RefObject<PlayerRef|null>;authority?:()=>SourceVideoReplacementAuthority}){
  const state=useCurrentStateAsRef();const write=useWriteContext();const [message,setMessage]=useState('');
  const [voiceReplacement,setVoiceReplacement]=useState<{source:CompanyVoiceClip;assetId:string}|null>(null);
  const [sceneVoice,setSceneVoice]=useState<{attachment:SceneVoiceAttachment;assetId:string;attachmentId:string}|null>(null);
  const sceneVoiceRef=useRef<typeof sceneVoice>(null);
  const releaseSceneVoice=useRef<(()=>void)|null>(null);
  useEffect(()=>()=>{releaseSceneVoice.current?.();},[]);
  useEffect(()=>{
    let live=true;
    const unsubscribe=subscribe(async media=>{
      const initial=state.current.undoableState;
      if(!(media.blob instanceof Blob)||media.blob.size!==media.size||media.blob.type!==media.mimeType||!media.filename||!/^https:\/\//.test(media.remoteUrl)) throw new Error('업로드 자료의 형식을 확인하지 못했습니다.');
      const {asset:originalAsset,item}=await makeItem({file:media.blob,filename:media.filename,remoteUrl:media.remoteUrl,remoteFileKey:media.remoteFileKey,
        fps:initial.fps,compositionWidth:initial.compositionWidth,compositionHeight:initial.compositionHeight,playerRef,dropPosition:null});
      const receipt=readVideoGenerationReceipt(media.videoGeneration);
      const asset=originalAsset.type==='video'&&receipt?{...originalAsset,wakaVideoGeneration:receipt}:originalAsset;
      flushSync(()=>{});
      if(media.sourceSceneGeneration&&media.attachmentId){
        if(!live||!authority||asset.type!=='video')throw Error('씬 생성 영상의 작업 연결을 확인해 주세요.');
        const plan=await receiveSourceSceneOutput(media,asset,{getState:()=>state.current,isLive:()=>live,authority,commit:(expected,next,mode)=>{
          let applied=false;flushSync(()=>write.setState({expectedUndoableState:expected,commitToUndoStack:true,update:current=>{if(!live)return current;const committed=commitSourceSceneOutput(current,expected,next,mode);if(!committed)return current;applied=true;return committed;}}));return applied;
        }});
        setMessage(plan.mode==='replaced'?'생성 영상으로 기존 씬을 교체했습니다. 한 번의 실행 취소로 되돌릴 수 있습니다. 저장해 주세요.':plan.mode==='already'?'이미 보관한 씬 생성 결과입니다.':`생성 결과를 자료함에 보관했습니다. ${plan.reason??'씬을 다시 선택해 보관 영상 다시 적용을 이용해 주세요.'}`);
        return {assetId:plan.assetId};
      }
      if(media.sceneVoice&&media.attachmentId){
        if(!live||asset.type!=='audio')throw Error('장면 음성 연결을 확인해 주세요.');
        if(sceneVoiceRef.current)throw Error('앞선 장면 음성 확인을 마친 뒤 다시 연결해 주세요.');
        const words=readSceneVoiceWords(media.sceneVoice.words,asset.durationInSeconds);
        const attachment={...media.sceneVoice,words},base=state.current;
        const existing=Object.values(base.undoableState.assets).find(value=>'wakaSpeechId' in value&&value.wakaSpeechId===media.attachmentId);
        if(existing&&(existing.type!=='audio'||existing.remoteUrl!==asset.remoteUrl||existing.durationInSeconds!==asset.durationInSeconds))throw Error('같은 결과 번호의 음성 원본이 다릅니다.');
        const retained=existing??{...asset,wakaSpeechId:media.attachmentId,wakaSpeech:{...attachment.settings,...(attachment.parts?{parts:attachment.parts}:{}),text:attachment.draftText,sourceKind:'scene',sceneKey:attachment.source.selection.sceneKey,sourceCaptionIds:[...attachment.source.captionIds],sourceCaptionTrackId:attachment.source.captionTrackId,sourceUrl:asset.remoteUrl}};
        if(!existing){
          if(base.undoableState.assets[retained.id])throw Error('같은 원본 번호가 있어 덮어쓰지 않았습니다.');
          const token=`retain-scene-voice:${crypto.randomUUID()}`;
          flushSync(()=>write.setState({expectedUndoableState:base.undoableState,mutationOwner:token,commitToUndoStack:true,update:current=>{
            if(!live||hasPendingTimelineEdit(current))return current;
            return {...current,undoableState:{...current.undoableState,assets:{...current.undoableState.assets,[retained.id]:retained}},assetStatus:{...current.assetStatus,[retained.id]:{type:'uploaded'}}};
          }}));
          if(state.current.documentMutationOwner!==token)throw Error('편집이 바뀌었습니다. 보관한 음성을 다시 연결해 주세요.');
        }
        sceneVoiceRef.current={attachment,assetId:retained.id,attachmentId:media.attachmentId};
        releaseSceneVoice.current=blockSceneVoiceStage(media.attachmentId);
        setSceneVoice(sceneVoiceRef.current);setMessage('새 음성을 보관했습니다. 인식된 자막과 교체 범위를 확인한 뒤 적용해 주세요.');
        return {assetId:retained.id};
      }
      if(media.voiceDraft&&media.attachmentId){
        if(!live)throw new Error('편집기 연결이 종료됐습니다.');
        const base=state.current,plan=planFreeSpeech(base,item,asset,media.voiceDraft,media.attachmentId);
        if(plan.mode!=='already'){
          let applied=false;
          flushSync(()=>write.setState({commitToUndoStack:true,update:current=>{
            if(!live||current.undoableState!==base.undoableState||hasPendingTimelineEdit(current))return current;
            applied=true;return {...current,undoableState:plan.state.undoableState,assetStatus:plan.state.assetStatus,selectedItems:plan.state.selectedItems,timelineTimeEditApplied:true};
          }}));
          if(!applied)throw new Error('편집 내용이 바뀌었습니다. 생성 음성을 다시 연결해 주세요.');
        }
        setMessage(plan.mode==='placed'?'지정한 시작점에 작성한 음성을 추가했습니다. 기존 트랙은 그대로입니다. 저장해 주세요.':plan.mode==='already'?'이미 연결한 음성입니다.':'생성 중 편집이 바뀌어 새 음성을 자료함에 보관했습니다. 원하는 위치에 배치하고 저장해 주세요.');
        return;
      }
      if(media.speech&&media.attachmentId){
        if(!live)throw new Error('편집기 연결이 종료됐습니다.');
        if(isCompanyVoiceClip(media.speech.source)){
          const base=state.current,retained=retainVoiceReplacement(base,asset,media.speech.source,media.speech.settings,media.attachmentId);
          if(retained.state!==base){
            let applied=false;
            flushSync(()=>write.setState({commitToUndoStack:true,update:current=>{
              if(!live||current.undoableState!==base.undoableState||hasPendingTimelineEdit(current))return current;
              applied=true;return {...current,undoableState:retained.state.undoableState,assetStatus:retained.state.assetStatus};
            }}));
            if(!applied)throw new Error('편집 내용이 바뀌었습니다. 새 음성을 다시 연결해 주세요.');
          }
          if(!retained.alreadyApplied)setVoiceReplacement({source:media.speech.source,assetId:retained.assetId});
          setMessage(retained.alreadyApplied?'이미 이 음성으로 교체했습니다.':'새 음성을 자료함에 보관했습니다. 길이 처리 방법을 고른 뒤 저장해 주세요.');
          return;
        }
        const base=state.current,plan=planGeneratedSpeech(base,item,asset,media.speech.source,media.speech.settings,media.attachmentId);
        if(plan.mode!=='already'){
          let applied=false;
          flushSync(()=>write.setState({commitToUndoStack:true,update:current=>{
            if(!live||current.undoableState!==base.undoableState||hasPendingTimelineEdit(current))return current;
            applied=true;return {...current,undoableState:plan.state.undoableState,assetStatus:plan.state.assetStatus,selectedItems:plan.state.selectedItems,timelineTimeEditApplied:true};
          }}));
          if(!applied)throw new Error('편집 내용이 바뀌었습니다. 생성 음성을 다시 연결해 주세요.');
        }
        setMessage(plan.mode==='placed'?'선택 자막 시작점에 새 음성 트랙을 추가했습니다. 기존 음성과 겹칠 수 있으니 확인하고 저장해 주세요.':plan.mode==='already'?'이미 연결한 음성입니다.':'자막이 바뀌었거나 음성이 구간보다 길어 자료함에만 보관했습니다. 직접 배치해 주세요.');
        return;
      }
      if((media.continueFrom||media.videoDraft)&&media.attachmentId){
        if(!live)throw new Error('편집기 연결이 종료됐습니다.');
        const base=state.current;
        const plan=media.videoDraft?planGeneratedVideoDraft(base,item,asset,media.videoDraft,media.attachmentId):planGeneratedContinuation(base,item,asset,media.continueFrom!,media.attachmentId);
        if(plan.mode!=='already'){
          let applied=false;
          flushSync(()=>write.setState({commitToUndoStack:true,update:current=>{
            if(!live||current.undoableState!==base.undoableState||hasPendingTimelineEdit(current))return current;
            applied=true;return {...current,undoableState:plan.state.undoableState,assetStatus:plan.state.assetStatus,selectedItems:plan.state.selectedItems,timelineTimeEditApplied:true};
          }}));
          if(!applied)throw new Error('편집 내용이 바뀌었습니다. 생성 결과를 다시 연결해 주세요.');
        }
        setMessage(plan.mode==='placed'?'생성 결과를 선택한 컷 뒤에 연결했습니다. 저장해 주세요.':plan.mode==='already'?'이미 연결한 생성 결과입니다.':'원래 컷이나 배치가 달라져 생성 결과를 자료함에만 추가했습니다. 원하는 위치에 놓아 주세요.');
        return;
      }
      if(!live||state.current.undoableState!==initial||hasPendingTimelineEdit(state.current)) throw new Error('파일을 읽는 동안 편집 내용이 바뀌어 추가하지 않았습니다.');
      if(media.librarySource){
        const base=state.current,result=retainCompanyLibraryAsset(base,asset,media.librarySource);
        if(!result.already){
          let applied=false;
          flushSync(()=>write.setState({commitToUndoStack:true,update:current=>{
            if(!live||current.undoableState!==base.undoableState||hasPendingTimelineEdit(current))return current;
            applied=true;return {...current,undoableState:result.state.undoableState,assetStatus:result.state.assetStatus};
          }}));
          if(!applied)throw new Error('편집 내용이 바뀌었습니다. 원본을 다시 추가해 주세요.');
        }
        setMessage(result.already?'이미 작업 자료함에 있는 원본입니다. 직접 배치해 주세요.':`${media.filename} 원본과 제작 정보를 자료함에 추가했습니다. 저장해 주세요.`);
        return;
      }
      // Upload adds an unplaced library source, never silently inserts time or overwrites an existing source.
      let applied=false;
      flushSync(()=>write.setState({commitToUndoStack:true,update:current=>{
        if(!live||current.undoableState!==initial||hasPendingTimelineEdit(current)||Object.hasOwn(current.undoableState.assets,asset.id))return current;
        applied=true;
        return {
        ...current,undoableState:{...current.undoableState,assets:{...current.undoableState.assets,[asset.id]:asset}},
        assetStatus:{...current.assetStatus,[asset.id]:{type:'uploaded'}},
      };}}));
      if(!applied)throw new Error('편집 내용이 바뀌어 파일 추가를 멈췄습니다. 다시 시도해 주세요.');
      setMessage(`${media.filename} 원본을 자료함에 추가했습니다. 저장 버튼으로 작업에 보관해 주세요.`);
      return {assetId:asset.id};
    });
    return ()=>{live=false;unsubscribe();};
  },[subscribe,playerRef,state,write,authority]);
  return <>{message&&<p role="status" className="px-3 text-sm">{message}</p>}
    {sceneVoice&&<SceneVoiceChooser key={sceneVoice.attachmentId} {...sceneVoice} onClose={applied=>{releaseSceneVoice.current?.();releaseSceneVoice.current=null;sceneVoiceRef.current=null;setSceneVoice(null);setMessage(applied?'장면의 내레이션과 자막을 교체했습니다. 한 번의 실행 취소로 되돌릴 수 있습니다. 저장해 주세요.':'기존 장면은 교체하지 않고 새 음성을 자료함에 보관했습니다. 저장해 주세요.');}}/>}
    {voiceReplacement&&<VoiceReplacementChooser key={voiceReplacement.assetId} {...voiceReplacement}
      onClose={()=>{setVoiceReplacement(null);setMessage('새 음성은 자료함에 보관했습니다. 저장 버튼으로 작업에 남겨 주세요.');}}
      onApplied={()=>{setVoiceReplacement(null);setMessage('선택한 오디오만 교체했습니다. 원본은 자료함에 남아 있으며 되돌릴 수 있습니다. 저장해 주세요.');}}/>}</>;
}
